/* eslint-disable jira/filename-case */
import type {
	ValueDecoration,
	LocalDecorationId,
} from '@jira/polaris__domain-field/src/ValueDecoration.types.tsx';
import type { DeliveryDateAggregationType } from '@jira/polaris__domain-field/src/DeliveryDateAggregationType.types.tsx';
import type { JiraFieldOptionWeight } from '@jira/polaris__domain-field/src/FieldOption.types.tsx';
import type { WeightType } from '@jira/polaris__domain-field/src/OptionFieldValue.types.tsx';
import type { StandardFieldType } from '@jira/polaris__domain-field/src/FieldType.types.tsx';
import type { ConnectionFieldIssueTypeFilter } from '@jira/polaris__domain-field/src/ConnectionFieldIssueTypeFilter.types.tsx';
import type { IntervalFieldSource } from '@jira/polaris__domain-field/src/IntervalFieldSources.tsx';
import type { DisplayMode } from '@jira/polaris__domain-field/src/DisplayMode.types.tsx';
import type {
	FieldKey,
	Field,
	FieldConfiguration,
	ConnectionFieldConfiguration,
} from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import type { DynamicFieldFormula } from '@jira/polaris__domain-field/src/DynamicFieldFormula.types.tsx';
import type { PolarisPlayKind } from '@jira/polaris__domain-field/src/PolarisPlayContribution.types.tsx';
import type { DeliveryFieldPresentationType } from '@jira/polaris__domain-field/src/DeliveryFieldPresentationType.types.tsx';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type { UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi, Action } from '@atlassian/react-sweet-state';
import type { State, Props, FieldsUpdate } from './FieldsUpdate.types.tsx';
import { associateGlobalFields } from './associateGlobalFields.tsx';
import {
	createField,
	createCalculatedField,
	createPlayField,
	updatePlayFieldParameters,
	createConnectionField,
} from './createConnectionField.tsx';
import { createValueDecoration, createValueDecorationBulk } from './createValueDecoration.tsx';
import { deleteValueDecoration, deleteAllValueDecorations } from './deleteValueDecoration.tsx';
import { updateValueDecoration } from './updateValueDecoration.tsx';
import { deleteField, deleteFieldLocally } from './deleteField.tsx';
import {
	throttledFetchSnippetProviders,
	fetchArchivedSnippetProviders,
} from './throttledFetchSnippetProviders.tsx';
import { captureLastFieldsUpdate } from './captureLastFieldsUpdate.tsx';
import { updateDeliveryDateAggregationType } from './updateDeliveryDateAggregationType.tsx';
import { updateDeliveryDateSelectedField } from './updateDeliveryDateSelectedField.tsx';
import { updateIntervalFieldSource } from './updateIntervalFieldSource.tsx';
import { loadFields } from './loadFields.tsx';
import { loadGlobalFieldsAssociations } from './loadGlobalFieldsAssociations.tsx';
import { loadGlobalLabels } from './loadGlobalLabels.tsx';
import { loadSelectedFields } from './loadSelectedFields.tsx';
import {
	resetNewlyAddedGlobalFields,
	setNewlyAddedGlobalFields,
} from './setNewlyAddedGlobalFields.tsx';
import { addOption } from './addOption.tsx';
import { deleteOption } from './deleteOption.tsx';
import { rankOptions } from './rankOptions.tsx';
import { renameOption } from './renameOption.tsx';
import { updateFieldOptionAutoFormattingDisabled } from './updateFieldOptionAutoFormattingDisabled.tsx';
import { updateFieldOptionWeightType } from './updateFieldOptionWeightType.tsx';
import { updateFieldOptionWeight } from './updateFieldOptionWeight.tsx';
import { createFieldOptionWeightBulk } from './createFieldOptionWeightBulk.tsx';
import { syncFieldsIssueTypes } from './syncFieldsIssueTypes.tsx';
import {
	updateConnectionFieldFilter,
	updateConnectionFieldIssueViewLayout,
	type ConnectionFieldIssueViewLayout,
} from './updateConnectionFieldFilter.tsx';
import { updateDeliveryFieldPresentation } from './updateDeliveryFieldPresentation.tsx';
import { updateDescription } from './updateDescription.tsx';
import { updateEmoji } from './updateEmoji.tsx';
import { updateFieldFormula } from './updateFieldFormula.tsx';
import { updateLabel } from './updateLabel.tsx';
import { updatePlayField } from './updatePlayField.tsx';
import { updateSystemFieldHidden } from './updateSystemFieldHidden.tsx';
import { updateUserFieldDisplayMode } from './updateUserFieldDisplayMode.tsx';
import type { UpdateValueDecorationProps } from './UpdateValueDecorationProps.types.tsx';

export type Actions = {
	addOption: (
		fieldKey: FieldKey,
		newOption: string,
	) => (
		{ getState, setState, dispatch }: StoreActionApi<State>,
		{ onFieldUpdateFailed, projectId, createAnalyticsEvent, fieldRemote }: Props,
	) => Promise<number | undefined>;
	createCalculatedField: (
		name: string,
		formula: DynamicFieldFormula,
		description?: string,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ issueTypeIds, fieldRemote, onFieldUpdateFailed }: Props,
	) => Promise<Field>;
	createField: (
		name: string,
		type: StandardFieldType,
		configuration?: FieldConfiguration,
		description?: string,
		options?: string[],
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ projectId, issueTypeIds, fieldRemote, onFieldUpdateFailed }: Props,
	) => Promise<Field>;
	createPlayField: (
		name: string,
		maxSpend: number,
		kind?: PolarisPlayKind,
		description?: string,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onActionFailed, onFieldUpdateFailed, cloudId, projectId, fieldRemote, issueTypeIds }: Props, // oxlint-disable-next-line typescript/no-invalid-void-type
	) => Promise<Field | void>;
	createConnectionField: (
		name: string,
		type: StandardFieldType,
		configuration?: ConnectionFieldConfiguration,
		description?: string,
	) => ({ dispatch }: StoreActionApi<State>) => Promise<Field>;
	createValueDecoration: (
		createProps: {
			fieldKey: FieldKey;
		} & Omit<ValueDecoration, 'localDecorationId'>,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ fieldRemote, onDecorationUpdateFailed, createAnalyticsEvent }: Props,
	) => Promise<LocalDecorationId>;
	createValueDecorationBulk: (
		createProps: {
			fieldKey: FieldKey;
		} & {
			decorations: Omit<ValueDecoration, 'localDecorationId'>[];
		},
	) => (stateParams: StoreActionApi<State>, propParams: Props) => LocalDecorationId[];
	deleteField: (
		fieldKey: FieldKey,
		onSuccess?: () => void,
		onError?: (e: Error) => void,
	) => (
		{ getState, dispatch }: StoreActionApi<State>,
		{
			onFieldUpdateFailed,
			deletePlay,
			createAnalyticsEvent,
			fieldRemote,
			globalFieldRemote,
			projectId,
		}: Props,
	) => Promise<void>;
	deleteFieldLocally: (
		fieldKey: FieldKey,
	) => ({ getState, setState }: StoreActionApi<State>) => Promise<void>;
	deleteOption: (
		fieldKey: FieldKey,
		jiraOptionId: string,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, projectId, createAnalyticsEvent, fieldRemote }: Props,
	) => Promise<void>;
	deleteValueDecoration: (
		fieldKey: FieldKey,
		localDecorationId: LocalDecorationId,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ fieldRemote, createAnalyticsEvent, onDecorationUpdateFailed }: Props,
	) => Promise<void>;
	deleteAllValueDecorations: ({
		fieldKey,
	}: {
		fieldKey: FieldKey;
	}) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, fieldRemote, createAnalyticsEvent }: Props,
	) => Promise<void>;
	throttledFetchSnippetProviders: (refetch?: boolean) => Action<State, Props>;
	fetchArchivedSnippetProviders: () => Action<State, Props>;
	loadFields: (forceReload?: boolean, scheduledReload?: boolean) => Action<State, Props>;
	loadSelectedFields: (
		fieldKeys: string[],
		loadOnlyIfFieldsKnown?: boolean,
	) => Action<State, Props, Promise<Field[]>>;
	rankOptions: (
		fieldKey: FieldKey,
		rankedOptionIds: string[],
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, projectId, createAnalyticsEvent, fieldRemote }: Props,
	) => Promise<void>;
	renameOption: (
		fieldKey: FieldKey,
		jiraOptionId: string,
		newValue: string,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, projectId, createAnalyticsEvent, fieldRemote }: Props,
	) => Promise<void>;
	updateFieldFormula: (
		fieldKey: FieldKey,
		formula: DynamicFieldFormula,
		abortSignal?: AbortSignal,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ fieldRemote, createAnalyticsEvent }: Props,
	) => Promise<Field>;
	updateDeliveryFieldPresentation: (
		fieldKey: FieldKey,
		presentation: DeliveryFieldPresentationType,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ fieldRemote, createAnalyticsEvent }: Props,
	) => Promise<void>;
	updateDescription: (
		fieldKey: string,
		description: string,
		createAnalyticsEvent: CreateUIAnalyticsEvent,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, fieldRemote }: Props,
	) => Promise<void>;
	updateEmoji: (
		fieldKey: string,
		emoji: string | undefined,
		createAnalyticsEvent: CreateUIAnalyticsEvent,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, fieldRemote }: Props,
	) => Promise<void>;
	updateFieldOptionWeight: (
		fieldKey: FieldKey,
		jiraOptionId: string,
		newWeight: number,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, projectId, createAnalyticsEvent, fieldRemote }: Props,
	) => Promise<void>;
	createFieldOptionWeightBulk: (
		fieldKey: FieldKey,
		options: JiraFieldOptionWeight[],
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, projectId, createAnalyticsEvent, fieldRemote }: Props,
	) => Promise<void>;
	updateFieldOptionWeightType: (
		fieldKey: FieldKey,
		newOptionWeightType: WeightType | undefined,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, fieldRemote, createAnalyticsEvent }: Props,
	) => Promise<Error | undefined>;
	updateFieldOptionAutoFormattingDisabled: (
		fieldKey: FieldKey,
		newValue: boolean,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, projectId, fieldRemote }: Props,
	) => Promise<void>;
	updateLabel: (
		fieldKey: FieldKey,
		label: string,
		createAnalyticsEvent: CreateUIAnalyticsEvent,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ fieldRemote }: Props,
	) => Promise<boolean | Error>;
	updateValueDecoration: (
		updateProps: UpdateValueDecorationProps,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ fieldRemote, onDecorationUpdateFailed, createAnalyticsEvent }: Props,
	) => void;
	updatePlayFieldParameters: (
		id: string,
		parameters: {
			maxSpend: number;
		},
	) => ({ getState, setState }: StoreActionApi<State>) => void;
	updatePlayField: (
		fieldKey: string,
		maxSpend: number,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, fieldRemote, createAnalyticsEvent }: Props,
	) => Promise<void>;
	updateUserFieldDisplayMode: (
		fieldKey: FieldKey,
		displayMode: DisplayMode,
		localUpdate?: boolean,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, fieldRemote, createAnalyticsEvent }: Props,
	) => Promise<void>;
	updateIntervalFieldSource: (
		fieldKey: FieldKey,
		source: IntervalFieldSource | undefined,
		contextAnalyticsEvent: UIAnalyticsEvent,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ fieldRemote }: Props, // oxlint-disable-next-line typescript/no-invalid-void-type
	) => Promise<Error | void>;
	updateDeliveryDateSelectedField: (
		fieldKey: FieldKey,
		selectedFieldKey: string | undefined,
		contextAnalyticsEvent: UIAnalyticsEvent,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ fieldRemote }: Props, // oxlint-disable-next-line typescript/no-invalid-void-type
	) => Promise<Error | void>;
	updateDeliveryDateAggregationType: (
		fieldKey: FieldKey,
		aggregationType: DeliveryDateAggregationType | undefined,
		contextAnalyticsEvent: UIAnalyticsEvent,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ fieldRemote }: Props, // oxlint-disable-next-line typescript/no-invalid-void-type
	) => Promise<Error | void>;
	updateSystemFieldHidden: (
		fieldKey: FieldKey,
		hidden: boolean,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, fieldRemote, createAnalyticsEvent }: Props,
	) => Promise<void>;
	associateGlobalFields: (fieldKeys: FieldKey[], dependencies?: FieldKey[]) => Action<State, Props>;
	setNewlyAddedGlobalFields: (fieldKeys: string[]) => Action<State, Props>;
	resetNewlyAddedGlobalFields: () => Action<State, Props>;
	captureLastFieldsUpdate: (lastFieldsUpdate: FieldsUpdate) => Action<State>;
	loadGlobalLabels: (fieldKey: string) => Action<State, Props, Promise<void>>;
	updateConnectionFieldFilter: (
		fieldKey: FieldKey,
		filter: ConnectionFieldIssueTypeFilter | null,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, fieldRemote }: Props,
	) => Promise<void>;
	updateConnectionFieldIssueViewLayout: (
		fieldKey: FieldKey,
		config: ConnectionFieldIssueViewLayout,
	) => (
		{ getState, setState }: StoreActionApi<State>,
		{ onFieldUpdateFailed, fieldRemote, globalFieldRemote, isSharedView }: Props,
	) => Promise<void>;
	syncFieldsIssueTypes: () => Action<State, Props>;
	loadGlobalFieldsAssociations: (fieldKeys: FieldKey[]) => Action<State, Props>;
};

export const actions: Actions = {
	addOption,
	createCalculatedField,
	createField,
	createPlayField,
	createConnectionField,
	createValueDecoration,
	createValueDecorationBulk,
	deleteField,
	deleteFieldLocally,
	deleteOption,
	deleteValueDecoration,
	deleteAllValueDecorations,
	throttledFetchSnippetProviders,
	fetchArchivedSnippetProviders,
	loadFields,
	loadSelectedFields,
	rankOptions,
	renameOption,
	updateFieldFormula,
	updateDeliveryFieldPresentation,
	updateDescription,
	updateEmoji,
	updateFieldOptionWeight,
	createFieldOptionWeightBulk,
	updateFieldOptionWeightType,
	updateFieldOptionAutoFormattingDisabled,
	updateLabel,
	updateValueDecoration,
	updatePlayFieldParameters,
	updatePlayField,
	updateUserFieldDisplayMode,
	updateIntervalFieldSource,
	updateDeliveryDateSelectedField,
	updateDeliveryDateAggregationType,
	updateSystemFieldHidden,
	associateGlobalFields,
	setNewlyAddedGlobalFields,
	resetNewlyAddedGlobalFields,
	captureLastFieldsUpdate,
	loadGlobalLabels,
	updateConnectionFieldFilter,
	updateConnectionFieldIssueViewLayout,
	syncFieldsIssueTypes,
	loadGlobalFieldsAssociations,
};
